/**
 * `buyerSamples.*` — a signed-in storefront buyer asking for physical samples.
 *
 * The buyer supplies **what** and **where**, and nothing else. Value is
 * computed server-side from the listing's own pricing; purpose, return date,
 * courier and the document number are the seller's calls, made later in the
 * admin queue. A sample is a request, not a transaction — no payment, no
 * commitment on either side until staff dispatch it.
 *
 * Quantity is in **pieces**, not packs: the rest of the store sells in whole
 * packs (spec 13), but asking a buyer to take a 6-piece pack to evaluate a
 * t-shirt defeats the purpose.
 *
 * @see packages/domain/src/commerce/store/request-sample.ts
 */
import { z } from "zod";
import { uuid } from "../_common.js";

/**
 * Caps mirror the domain constants. They exist so "request a sample" cannot
 * quietly become "ship me a free order" — the boundary refuses the shape, and
 * the domain refuses it again for callers that aren't this one.
 */
export const MAX_SAMPLE_PIECES_PER_LINE = 5;
export const MAX_SAMPLE_LINES = 10;

export const requestLine = z.object({
  listingId: uuid,
  qty: z.number().int().min(1).max(MAX_SAMPLE_PIECES_PER_LINE),
});

export const requestInput = z.object({
  storeSlug: z.string().min(1).max(64),
  lines: z.array(requestLine).min(1).max(MAX_SAMPLE_LINES),
  /** Where to send it. Free text, as on an order's ship-to. */
  shipTo: z.object({
    contactName: z.string().trim().min(1).max(160),
    phone: z.string().trim().min(10).max(10),
    address: z.string().trim().min(1).max(500),
    city: z.string().trim().min(1).max(64),
    state: z.string().trim().min(1).max(64),
    pin: z.string().trim().regex(/^\d{6}$/),
  }),
  /** What the buyer wants to check — colour, GSM, a specific shade match. */
  note: z.string().trim().max(1000).optional(),
});

export const requestOutput = z.object({
  sampleId: uuid,
  status: z.literal("requested"),
});

export type RequestSampleInput = z.infer<typeof requestInput>;

// ── history: the buyer reading their own samples (spec 44) ────────

/**
 * What the buyer reads is the **phase** — the stored status folded together
 * with the return date. `overdue` is derived on every read and never stored,
 * for the same reason a quote's `expired` isn't: it would go stale at midnight
 * with nothing but a cron to flip it.
 *
 * Wording is buyer-facing and deliberately not the stored vocabulary:
 * `dispatched` reads as "On the way", `converted` as "Converted to order".
 * The screens own the labels; this enum owns the states.
 */
export const buyerSamplePhase = z.enum([
  "requested",
  "dispatched",
  "delivered",
  "converted",
  "returned",
  "overdue",
]);

export const samplePurpose = z.enum(["evaluation", "photoshoot", "trade_show"]);

/**
 * A row on the buyer's list. **No money**: a sample is not billed, the request
 * form promises "No payment", and a rupee figure here would read as one
 * (spec 44 R4). The value the admin tracks is stock-at-risk, which is the
 * seller's concern and not the buyer's.
 */
export const buyerSampleCard = z.object({
  sampleId: uuid,
  /** `null` until dispatch mints one — the screen shows "Requested" instead. */
  docNumber: z.string().nullable(),
  phase: buyerSamplePhase,
  purpose: samplePurpose,
  requestedAt: z.string().datetime(),
  /** Date-only. The one thing on this screen the buyer has to act on. */
  returnBy: z.string().nullable(),
  returnedAt: z.string().datetime().nullable(),
  courier: z.string().nullable(),
  awb: z.string().nullable(),
  /** Set once the sample became an order — the card links through to it. */
  convertedOrderId: uuid.nullable(),
  pieceCount: z.number().int(),
  lineCount: z.number().int(),
  firstLineLabel: z.string().nullable(),
});

export const listInput = z.object({
  storeSlug: z.string().min(1).max(64),
  cursor: z.string().nullish(),
  limit: z.number().int().min(1).max(50).default(20),
});

export const listOutput = z.object({
  samples: z.array(buyerSampleCard),
  nextCursor: z.string().nullable(),
});

export const getInput = z.object({
  storeSlug: z.string().min(1).max(64),
  sampleId: uuid,
});

export const buyerSampleLine = z.object({
  lineId: uuid,
  position: z.number().int(),
  qty: z.number().int(),
  /**
   * Per line, because one sample can mix pieces that go back with pieces the
   * buyer keeps — so "do I owe this?" is not a property of the sample.
   */
  returnExpected: z.boolean(),
  itemName: z.string().nullable(),
  sku: z.string().nullable(),
  /** Null when the listing was unpublished since — the line loses its link only. */
  listingSlug: z.string().nullable(),
});

export const buyerSampleEvent = z.object({
  key: z.enum(["requested", "dispatched", "delivered", "returned", "converted"]),
  /** Reached and untimed are different: `delivered` has a status but no column. */
  reached: z.boolean(),
  at: z.string().datetime().nullable(),
});

export const getOutput = z.object({
  sampleId: uuid,
  docNumber: z.string().nullable(),
  phase: buyerSamplePhase,
  purpose: samplePurpose,
  requestedAt: z.string().datetime(),
  returnBy: z.string().nullable(),
  dispatchedAt: z.string().datetime().nullable(),
  returnedAt: z.string().datetime().nullable(),
  courier: z.string().nullable(),
  awb: z.string().nullable(),
  shipToText: z.string().nullable(),
  /**
   * The seller's public phone (0149 view), for the contact card's WhatsApp
   * link. `null` when the store recorded none — the screen omits the card
   * rather than drawing a control that opens nothing.
   */
  storePhone: z.string().nullable(),
  /** The buyer's own words, handed back: what they asked to be checked. */
  note: z.string().nullable(),
  convertedOrderId: uuid.nullable(),
  /** Pieces still owed back — summed over the lines, not stored. */
  onLoanPieces: z.number().int(),
  pieceCount: z.number().int(),
  lines: z.array(buyerSampleLine),
  timeline: z.array(buyerSampleEvent),
});
